import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import type { IFormElement } from '../types';
import { useFormState } from '../contexts/FormStateContext';
import styles from './fields.module.css';

export interface TextAreaFieldProps {
  element: IFormElement;
  questionNumber?: number;
}

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  element,
  questionNumber,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const formState = useFormState();
  const error = errors[element.name];
  const validationError = formState.validationErrors[element.name];

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label
        className={`${styles.label} ${
          element.isRequired ? styles.required : ''
        }`}
        htmlFor={element.name}
      >
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <Controller
        name={element.name}
        control={control}
        defaultValue={element.defaultValue || ''}
        render={({ field }) => (
          <textarea
            {...field}
            id={element.name}
            className={`${styles.input} ${styles.textarea} ${
              error || validationError ? styles.error : ''
            }`}
            placeholder={element.placeholder || ''}
            disabled={element.readOnly}
            maxLength={element.maxLength}
            rows={typeof element.rows === 'number' ? element.rows : 4}
            value={field.value || ''}
            onChange={(e) => {
              e.stopPropagation();
              field.onChange(e.target.value);
              if (validationError) {
                formState.clearValidationError(element.name);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            onBlur={(e) => {
              e.stopPropagation();
              field.onBlur();
              formState.markFieldAsTouched(element.name);
            }}
          />
        )}
      />
      {(error || validationError) && (
        <p className={styles.errorMessage}>
          {(error?.message as string) || validationError}
        </p>
      )}
    </div>
  );
};

export default TextAreaField;
